import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ChevronDown, LayoutDashboard, LogOut, Menu, Sparkles, User } from "lucide-react";

export default function Navbar({ variant = "adult" }) {
  const { user, logout } = useAuth();
  const { currency, setCurrency, CURRENCIES } = useCurrency();
  const nav = useNavigate();
  const loc = useLocation();

  const isKids = variant === "kids";
  const linkBase = "text-sm tracking-wide transition-smooth";
  const linkAdult = "text-zinc-400 hover:text-white";
  const linkKids = "text-slate-600 hover:text-[#FF6B6B]";
  const activeAdult = "text-white";
  const activeKids = "text-[#FF6B6B]";

  const navLinks = [
    { to: "/flagship", label: "Weekend Bootcamp" },
    { to: "/courses", label: "Mini Courses" },
    { to: "/adults", label: "For Adults" },
    { to: "/kids", label: "For Kids" },
    { to: "/meet-sankalp", label: "Meet Sankalp" },
  ];

  const currencyBtn = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="currency-switcher"
          className={
            isKids
              ? "flex items-center gap-1 whitespace-nowrap shrink-0 rounded-full px-3 py-1.5 bg-white kids-shadow text-xs font-semibold"
              : "flex items-center gap-1 whitespace-nowrap shrink-0 rounded-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-smooth text-xs font-adult-mono tracking-wide"
          }
        >
          {CURRENCIES[currency].symbol} {CURRENCIES[currency].label}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel className="text-xs uppercase tracking-[0.2em]">Currency</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {Object.entries(CURRENCIES).map(([k, meta]) => (
          <DropdownMenuItem
            key={k}
            data-testid={`currency-option-${k}`}
            onClick={() => setCurrency(k)}
            className={currency === k ? "font-semibold" : ""}
          >
            {meta.symbol} {meta.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header
      data-testid="app-navbar"
      className={
        isKids
          ? "sticky top-0 z-50 backdrop-blur-xl bg-[#FDF8F5]/80 border-b border-[#F3E8E0]"
          : "sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-zinc-800"
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        <BrandLogo light={isKids} />

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `${linkBase} ${isKids ? linkKids : linkAdult} ${isActive ? (isKids ? activeKids : activeAdult) : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {currencyBtn}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className={isKids ? "hidden md:flex items-center gap-2 rounded-full px-3 py-2 bg-white kids-shadow" : "hidden md:flex items-center gap-2 rounded-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-smooth"}>
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className={isKids ? "w-6 h-6 rounded-full bg-[#4ECDC4] text-white text-xs flex items-center justify-center font-bold" : "w-6 h-6 rounded-full bg-amber-500 text-black text-xs flex items-center justify-center font-semibold"}>
                      {(user.name || user.email)[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm max-w-[120px] truncate hidden sm:inline">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>
                    <Sparkles className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                onClick={() => nav(`/login?next=${encodeURIComponent(loc.pathname)}`)}
                className={isKids ? "text-[#2B2D42] hover:text-[#FF6B6B]" : "text-zinc-300 hover:text-white"}
              >
                <User className="w-4 h-4 mr-1.5" /> Log in
              </Button>
              <Button
                data-testid="nav-signup-btn"
                onClick={() => nav("/signup")}
                className={isKids ? "bg-[#FF6B6B] hover:bg-[#e85a5a] text-white rounded-full px-5 kids-shadow" : "bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-5 glow-amber"}
              >
                Get Started
              </Button>
            </div>
          )}
          <MobileMenu links={navLinks} isKids={isKids} user={user} onLogin={() => nav(`/login?next=${encodeURIComponent(loc.pathname)}`)} onSignup={() => nav("/signup")} onNav={nav} onLogout={async () => { await logout(); nav("/"); }} />
        </div>
      </div>
    </header>
  );
}

function MobileUserLinks({ user, isKids, item, go, onNav, onLogout }) {
  return (
    <div className={`mt-8 pt-6 border-t flex flex-col gap-1 ${isKids ? "border-[#F3E8E0]" : "border-zinc-800"}`}>
      <div className={`px-4 pb-2 text-xs truncate ${isKids ? "text-[#8D99AE]" : "text-zinc-500"}`}>{user.email}</div>
      <button data-testid="mobile-nav-dashboard" onClick={go(() => onNav("/dashboard"))} className={`${item} text-left flex items-center gap-2`}><LayoutDashboard className="w-4 h-4" />Dashboard</button>
      {user.role === "admin" && <button data-testid="mobile-nav-admin" onClick={go(() => onNav("/admin"))} className={`${item} text-left flex items-center gap-2`}><Sparkles className="w-4 h-4" />Admin</button>}
      <button data-testid="mobile-nav-logout" onClick={go(onLogout)} className={`${item} text-left flex items-center gap-2`}><LogOut className="w-4 h-4" />Sign out</button>
    </div>
  );
}

function MobileMenu({ links, isKids, user, onLogin, onSignup, onNav, onLogout }) {
  const [open, setOpen] = useState(false);
  const go = (fn) => () => { setOpen(false); fn(); };
  const item = isKids
    ? "rounded-xl px-4 py-3 text-base font-semibold text-[#2B2D42] hover:bg-white"
    : "rounded-xl px-4 py-3 text-base text-zinc-300 hover:bg-zinc-900 hover:text-white";
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="nav-mobile-menu-btn" aria-label="Open menu" className={`md:hidden w-9 h-9 rounded-full flex items-center justify-center ${isKids ? "bg-white kids-shadow text-[#2B2D42]" : "bg-zinc-900 border border-zinc-800 text-zinc-200"}`}>
          <Menu className="w-4 h-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="nav-mobile-menu" className={`w-[82vw] max-w-sm ${isKids ? "bg-[#FDF8F5] text-[#2B2D42] border-[#F3E8E0]" : "bg-[#0b0b0d] text-white border-zinc-800"}`}>
        <SheetTitle className="sr-only">Code With Sankalp menu</SheetTitle>
        <BrandLogo light={isKids} testid="mobile-nav-brand-logo" onClick={() => setOpen(false)} />
        <nav className="mt-6 flex flex-col gap-1">
          {links.map((l) => (
            <SheetClose asChild key={l.to}>
              <NavLink to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={item}>{l.label}</NavLink>
            </SheetClose>
          ))}
        </nav>
        {user && <MobileUserLinks user={user} isKids={isKids} item={item} go={go} onNav={onNav} onLogout={onLogout} />}
        {!user && (
          <div className="mt-8 flex flex-col gap-3">
            <SheetClose asChild>
              <Button data-testid="mobile-nav-login-btn" variant="outline" onClick={onLogin} className={isKids ? "rounded-full border-[#F3E8E0] bg-white text-[#2B2D42]" : "rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-900"}>
                <User className="w-4 h-4 mr-1.5" /> Log in
              </Button>
            </SheetClose>
            <SheetClose asChild>
              <Button data-testid="mobile-nav-signup-btn" onClick={onSignup} className={isKids ? "rounded-full bg-[#FF6B6B] hover:bg-[#e85a5a] text-white" : "rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"}>
                Get Started
              </Button>
            </SheetClose>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
